import { createNavigationLookups } from '../../resolution/navigation/navigation-lookups.js';
import { buildNavigationContext, refHasResolvableDefinitions } from '../../resolution/resolve-definitions.js';
import type { DbTagRow } from '../../resolution/types.js';
import type { CodeNavSymbolSpan } from '../../types.js';
import { listWorkspaceFiles } from '../build/workspace-files.js';
import { getSymbolIndexParentContext } from '../sqlite/symbol-index-sqlite.js';
import { loadFileTags } from '../storage/symbol-parent-file-map.js';
import { emptyRefRow } from './tag-query-shared.js';
import { fastTagIsClickable, rowToSymbolSpan } from './symbol-span-from-row.js';

export type SymbolQuerySnapshot = {
  rows: DbTagRow[];
  lookups: ReturnType<typeof createNavigationLookups>;
  workspaceFiles: readonly string[];
};

export function loadFileTagRows(
  db: Parameters<typeof getSymbolIndexParentContext>[0],
  resolved: string,
  filePath: string,
): DbTagRow[] | null {
  const ctx = getSymbolIndexParentContext(db, resolved);
  if (ctx == null) return null;

  return loadFileTags(db, ctx, filePath);
}

export function loadSymbolQuerySnapshot(
  db: Parameters<typeof getSymbolIndexParentContext>[0],
  resolved: string,
  filePath: string,
): SymbolQuerySnapshot | null {
  const ctx = getSymbolIndexParentContext(db, resolved);
  if (ctx == null) return null;

  const rows = loadFileTags(db, ctx, filePath);
  if (rows.length === 0) return null;

  return {
    rows,
    lookups: createNavigationLookups(db, ctx.parentId),
    workspaceFiles: listWorkspaceFiles(db, resolved, filePath),
  };
}

export function spansFromSnapshotFast(rows: DbTagRow[]): CodeNavSymbolSpan[] {
  return rows.map((row) => rowToSymbolSpan(row, fastTagIsClickable(row)));
}

export function spansFromSnapshot(filePath: string, snapshot: SymbolQuerySnapshot): CodeNavSymbolSpan[] {
  const baseCtx = buildNavigationContext(
    filePath,
    snapshot.rows[0] ?? emptyRefRow(filePath),
    snapshot.lookups,
    snapshot.workspaceFiles,
  );
  return snapshot.rows.map((row) => {
    const navCtx = { ...baseCtx, ref: row };
    return rowToSymbolSpan(row, refHasResolvableDefinitions(navCtx));
  });
}
